import React from 'react';

/**
 * Accessible segmented control for small option lists (theme, language).
 *
 * Built on native radio inputs so arrow-key navigation, focus, and
 * screen-reader semantics come from the platform instead of being
 * reimplemented. Each segment is a `<label>` wrapping a visually
 * hidden radio; the selected segment is styled from the controlled
 * `value`. Changes fire immediately on selection.
 */
function SegmentedControl({
  name,
  label,
  options = [],
  value,
  onChange,
  disabled = false,
}) {
  if (!options.length) return null;

  return (
    <div
      role="radiogroup"
      aria-label={label}
      className="inline-flex max-w-full items-center gap-1 rounded-xl border border-gray-200 dark:border-neutral-700 bg-gray-50 dark:bg-neutral-900 p-1"
    >
      {options.map((option) => {
        const selected = value === option.value;
        return (
          <label
            key={option.value}
            className={`relative inline-flex min-h-[40px] cursor-pointer items-center justify-center rounded-lg px-3 py-1.5 text-sm font-medium transition-colors has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-green-600 has-[:focus-visible]:ring-offset-1 has-[:focus-visible]:ring-offset-white dark:has-[:focus-visible]:ring-offset-black ${
              selected
                ? 'bg-white text-gray-900 shadow-sm dark:bg-neutral-700 dark:text-white'
                : 'text-gray-600 hover:text-gray-900 dark:text-gray-300 dark:hover:text-white'
            } ${disabled ? 'cursor-not-allowed opacity-60' : ''}`}
          >
            <input
              type="radio"
              name={name}
              value={option.value}
              checked={selected}
              onChange={() => onChange(option.value)}
              disabled={disabled}
              className="sr-only"
            />
            <span>{option.label}</span>
          </label>
        );
      })}
    </div>
  );
}

export default SegmentedControl;
